import React, { useState } from 'react';
import Breadcrumb from '../../components/common/Breadcrumb';
import toast from 'react-hot-toast';
import { IconPlus, IconX, IconCheck, IconEye, IconTrash } from '@tabler/icons-react';
import { usePayrollRuns, useTriggerPayrollRun, useDeletePayrollRun } from '../../hooks/usePayroll';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { PageLoader, TableLoader } from '../../components/common/Loader';
import AssigneeSearchField from './components/AssigneeSearchField';
import ReviewerChainField from './components/ReviewerChainField';
import ActionDropdown from '../../components/common/ActionDropdown';
import ConfirmDeleteModal from '../../components/common/ConfirmDeleteModal';

const STATUS_STYLES = {
  draft: 'bg-warning/10 text-warning',
  in_review: 'bg-sky/10 text-sky',
  submitted: 'bg-sky/10 text-sky',
  approved: 'bg-success/10 text-success',
  published: 'bg-purple-500/10 text-purple-500',
  rejected: 'bg-danger/10 text-danger',
};

function TriggerRunModal({ isOpen, onClose, orgId }) {
  const [label, setLabel] = useState('');
  const [notes, setNotes] = useState('');
  const [reviewers, setReviewers] = useState([]);
  const [approveAssignee, setApproveAssignee] = useState(null);
  const triggerRun = useTriggerPayrollRun();
  const navigate = useNavigate();

  if (!isOpen) return null;

  const handleSubmit = () => {
    if (!label.trim()) { toast.error('Run label is required.'); return; }
    if (!reviewers.length) { toast.error('Please add at least one reviewer.'); return; }
    if (!approveAssignee) { toast.error('Please select an approver.'); return; }

    triggerRun.mutate({
      data: {
        label: label.trim(),
        notes: notes.trim(),
        reviewer_ids: reviewers.map((r) => r.id),
        approve_assignee_id: approveAssignee.id,
      },
      orgId,
    }, {
      onSuccess: (run) => {
        toast.success(`Payroll run "${run.label}" created with ${run.record_count} records.`);
        onClose();
        navigate(`/payroll/runs/${run.id}`);
      },
      onError: (err) => toast.error(err?.response?.data?.error || err?.response?.data?.detail || 'Failed to trigger run.'),
    });
  };

  return (
    <>
      <div className="fixed inset-0 z-[1001] flex items-center justify-center p-15">
        <div className="bg-bg-dark rounded-lg shadow-light border border-border w-full max-w-[480px]">
          <div className="flex items-center justify-between p-20 border-b border-border">
            <h3 className="text-[16px]/[20px] font-semibold">Trigger New Payroll Run</h3>
            <button onClick={onClose} className="text-text-secondary hover:text-text-primary">
              <IconX size={20} />
            </button>
          </div>
          <div className="p-20 flex flex-col gap-15">
            <p className="text-sm text-text-secondary">
              This will snapshot the current active template into a frozen payroll run.
              Make sure the template is up to date before proceeding.
            </p>
            <div>
              <label className="block text-sm font-medium mb-5">
                Run Label <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="e.g. March 2026 Payroll"
                className="w-full bg-bg-light border border-border rounded-[4px] px-10 py-[8px] text-sm focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-5">Notes (optional)</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Any notes..."
                rows={3}
                className="w-full bg-bg-light border border-border rounded-[4px] px-10 py-[8px] text-sm focus:outline-none resize-none"
              />
            </div>

            <ReviewerChainField
              required
              orgId={orgId}
              value={reviewers}
              onChange={setReviewers}
              excludeIds={approveAssignee ? [approveAssignee.id] : []}
            />
            <AssigneeSearchField
              label="Approver"
              required
              orgId={orgId}
              value={approveAssignee}
              onSelect={setApproveAssignee}
              onClear={() => setApproveAssignee(null)}
              excludeIds={reviewers.map((r) => r.id)}
            />
            <p className="text-xs text-text-secondary -mt-8">
              Reviewers act in order, one at a time; once all have reviewed, the approver approves or rejects it. Anyone may be from another organisation.
            </p>
          </div>
          <div className="flex gap-10 p-20 border-t border-border">
            <button onClick={onClose} className="btn btn-outline-secondary flex-1" disabled={triggerRun.isPending}>
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={triggerRun.isPending || !label.trim() || !reviewers.length || !approveAssignee}
              className="btn btn-primary flex-1 flex items-center justify-center gap-5 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <IconCheck size={16} />
              {triggerRun.isPending ? 'Creating...' : 'Trigger Run'}
            </button>
          </div>
        </div>
      </div>
      <div onClick={onClose} className="fixed z-[1000] inset-0 bg-black/50 backdrop-blur-[3px]" />
    </>
  );
}

export default function PayrollRuns() {
  const orgId = localStorage.getItem('orgId') || '';
  const { hasAnyPermission } = useAuth();
  const navigate = useNavigate();
  const [showTrigger, setShowTrigger] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const canView = hasAnyPermission(['rbacapp.pay_view_all', 'rbacapp.pay_review', 'rbacapp.pay_approve', 'rbacapp.pay_publish']);
  const canTrigger = hasAnyPermission(['rbacapp.pay_upload', 'rbacapp.pay_review']);
  const isHR = hasAnyPermission(['rbacapp.pay_review']);
  const canPublish = hasAnyPermission(['rbacapp.pay_publish']);

  const { data: runs, isLoading } = usePayrollRuns(orgId, { enabled: canView });
  const deleteRun = useDeletePayrollRun();

  const activeDraftRun = runs?.find(r => r.status === 'draft');

  const canDeleteRun = (run) => (run.status === 'published' ? canPublish : isHR);

  const handleDelete = () => {
    deleteRun.mutate({ id: deleteTarget.id, orgId }, {
      onSuccess: (data) => { toast.success(data?.message || 'Run deleted.'); setDeleteTarget(null); },
      onError: (err) => {
        const status = err?.response?.status;
        const fallback = status === 403
          ? 'You do not have permission to delete this run.'
          : status === 404
            ? 'Run not found.'
            : 'Delete failed.';
        toast.error(err?.response?.data?.error || err?.response?.data?.detail || fallback);
        setDeleteTarget(null);
      },
    });
  };

  return (
    <>
      <Breadcrumb title="Payroll Runs" path="Payroll / Runs" />

      <div className="bg-bg-dark rounded-lg shadow-light md:p-20 p-15">
        <div className="flex items-center justify-between gap-15 flex-wrap mb-20">
          <div>
            <h2 className="text-[16px]/[20px] font-semibold">All Payroll Runs</h2>
            <p className="text-text-secondary text-sm mt-3">
              Each run is a frozen snapshot of the template at a point in time.
            </p>
          </div>
          {canTrigger && (
            <div className="flex flex-col items-end gap-4">
              <button
                onClick={() => setShowTrigger(true)}
                disabled={!!activeDraftRun}
                className="btn btn-primary flex items-center gap-5 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <IconPlus size={16} />
                Trigger New Run
              </button>
              {activeDraftRun && (
                <p className="text-xs text-text-secondary">
                  <span className="font-medium">{activeDraftRun.label}</span> is in draft — submit it before triggering a new run.
                </p>
              )}
            </div>
          )}
        </div>

        {isLoading ? (
          <TableLoader text="Loading runs..." />
        ) : !runs?.length ? (
          <TableLoader text="
            No payroll runs yet. Trigger a run after uploading a template.
          " />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[860px]">
              <thead>
                <tr className="bg-bg-light-100">
                  <th align="left" className="p-10 font-medium">Label</th>
                  <th align="center" className="p-10 font-medium">Status</th>
                  <th align="left" className="p-10 font-medium">Current Reviewer</th>
                  <th align="left" className="p-10 font-medium">Approver</th>
                  <th align="left" className="p-10 font-medium">Triggered By</th>
                  <th align="center" className="p-10 font-medium">Records</th>
                  <th align="left" className="p-10 font-medium">Date</th>
                  <th align="center" className="p-10 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {runs.map((run) => (
                  <tr
                    key={run.id}
                    className="border-t border-border hover:bg-bg-light cursor-pointer"
                    onClick={() => navigate(`/payroll/runs/${run.id}`)}
                  >
                    <td className="p-10 font-medium">{run.label}</td>
                    <td className="p-10 text-center">
                      <span className={`px-8 py-3 rounded-full text-xs capitalize ${STATUS_STYLES[run.status] || ''}`}>
                        {run.status}
                      </span>
                    </td>
                    <td className="p-10 text-text-secondary">{run.current_reviewer_name || '—'}</td>
                    <td className="p-10 text-text-secondary">{run.approve_assignee_name || '—'}</td>
                    <td className="p-10 text-text-secondary">{run.triggered_by_name || '—'}</td>
                    <td className="p-10 text-center">{run.record_count}</td>
                    <td className="p-10 text-text-secondary">
                      {new Date(run.created_at).toLocaleDateString('en-GB', {
                        day: '2-digit', month: 'short', year: 'numeric',
                      })}
                    </td>
                    <td className="p-10 text-center" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-center">
                        <ActionDropdown
                          menuItems={[
                            {
                              label: 'View',
                              icon: IconEye,
                              description: 'Open the full run detail',
                              onClick: () => navigate(`/payroll/runs/${run.id}`),
                            },
                            ...(canDeleteRun(run) ? [{
                              label: 'Delete Run',
                              icon: IconTrash,
                              danger: true,
                              description: 'Delete this run and all its payslips',
                              onClick: () => setDeleteTarget(run),
                            }] : []),
                          ]}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <TriggerRunModal
        isOpen={showTrigger}
        onClose={() => setShowTrigger(false)}
        orgId={orgId}
      />

      <ConfirmDeleteModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        isLoading={deleteRun.isPending}
        title="Delete Payroll Run"
        message={
          deleteTarget?.status === 'published'
            ? `"${deleteTarget?.label}" is published and its payslips have already been issued to employees. Deleting will remove the run, its records, review steps, and every payslip (and payslip line) tied to it. This cannot be undone.`
            : `You are about to permanently delete "${deleteTarget?.label}" and its ${deleteTarget?.record_count} records, along with any review steps and payslips generated for it. This cannot be undone.`
        }
        confirmLabel="Delete Run"
        loadingLabel="Deleting..."
      />
    </>
  );
}
